import React from "react";
import { HiPlus } from "react-icons/hi";
import { ActivityType } from "../../../types/activity";
import { PerformanceLog, PerformanceType } from "../../../types/post";
import ResultRowEditor from "./ResultRowEditor";
import { ResultRowState, emptyRow } from "./performanceDraft";

interface ResultsStepProps {
  performanceType: PerformanceType;
  rows: ResultRowState[];
  activityTypes: ActivityType[];
  loadingActivityTypes: boolean;
  onRowsChange: (rows: ResultRowState[]) => void;
  onActivityCreated: (activityTypeId: string) => Promise<ActivityType | null>;
  /** The performance's own notes (saved on the training log). */
  notes: string;
  onNotesChange: (notes: string) => void;
  isPremium?: boolean;
  pastPerformanceLogs?: (PerformanceLog & { id: string })[];
  historyReady?: boolean;
  historyLoadFailed?: boolean;
}

/** Step 3 — 0..n result rows (a Game Day may have none). */
const ResultsStep: React.FC<ResultsStepProps> = ({
  performanceType,
  rows,
  activityTypes,
  loadingActivityTypes,
  onRowsChange,
  onActivityCreated,
  notes,
  onNotesChange,
  isPremium = false,
  pastPerformanceLogs = [],
  historyReady = false,
  historyLoadFailed = false,
}) => {
  const updateRow = (key: string, next: ResultRowState) =>
    onRowsChange(rows.map((r) => (r.key === key ? next : r)));
  const removeRow = (key: string) =>
    onRowsChange(rows.filter((r) => r.key !== key));

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-bold font-grotesk text-base-content">
          Results
        </h3>
        <p className="text-sm text-base-content/60 font-grotesk">
          {performanceType === "gameDay"
            ? "Optional for a game day — add measured results, notes, or skip to load."
            : "Add each measured result. Rows seeded from a session show your target."}
        </p>
      </div>

      {rows.map((row, index) => (
        <ResultRowEditor
          key={row.key}
          row={row}
          index={index}
          activityTypes={activityTypes}
          loadingActivityTypes={loadingActivityTypes}
          onChange={(next) => updateRow(row.key, next)}
          onRemove={() => removeRow(row.key)}
          onActivityCreated={onActivityCreated}
          isPremium={isPremium}
          pastPerformanceLogs={pastPerformanceLogs}
          historyReady={historyReady}
          historyLoadFailed={historyLoadFailed}
        />
      ))}

      <div className="relative pt-3">
        <label
          htmlFor="performance-notes"
          className="absolute left-4 top-5 text-[11px] font-medium uppercase tracking-wider text-base-content/50 pointer-events-none z-10"
        >
          Notes
        </label>
        <textarea
          id="performance-notes"
          className="px-4 pt-6 pb-2.5 rounded-md bg-base-200 border border-transparent focus:border-base-300 h-28 resize-none focus:outline-none focus:ring-0 text-[16px] w-full placeholder:text-base-content/40"
          placeholder={
            performanceType === "gameDay"
              ? "How did the game go?"
              : "Anything worth remembering about this performance..."
          }
          value={notes}
          onChange={(e) => onNotesChange(e.target.value)}
        />
      </div>

      <AddResultButton onClick={() => onRowsChange([...rows, emptyRow()])} />
    </div>
  );
};

const AddResultButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full text-sm bg-base-100 hover:bg-base-200 border border-base-300 py-3 px-4 rounded-md font-medium flex items-center justify-between transition-colors"
  >
    <span>Add result</span>
    <HiPlus className="w-4 h-4 text-base-content/50" />
  </button>
);

export default ResultsStep;
